import { AiStatus } from '../entity/api/ai';
import {
  type CreateSeoStrategyKeywordReq,
  type CreateSeoStrategyKeywordRes,
  type GetActionPlanByBrandIdRes,
  type GetSeoStrategyByBrandIdRes,
  type LinkSeoStrategyKeywordToContentReq,
  type LinkSeoStrategyKeywordToContentRes,
  type RegenerateSeoStrategyRes,
  type RemoveSeoStrategyKeywordRes,
  type SaveSeoStrategyReq,
  type SaveSeoStrategyRes,
  type SearchSeoStrategyKeywordsReq,
  type SearchSeoStrategyKeywordsRes,
  type SeoStrategyUpdateSocketRes,
  type UpdateActionByIdReq,
  type UpdateActionByIdRes,
  type UpdateSeoStrategyKeywordReq,
  type UpdateSeoStrategyKeywordRes,
  saveSeoStrategyReqSchema,
} from '../entity/api/routes/seo-strategy';
import { SeoStrategyStatus } from '../entity/api/seo-strategy';
import { InternalServerError } from '../error';
import { apiRequest, getQueryClient } from './request';

// NOTE: We store pending promises for seo strategy updates (save & regenerate both use this)
const pendingSeoStrategyUpdates = new Map<
  string,
  {
    resolve: (value: SeoStrategyUpdateSocketRes) => void;
    reject: (reason?: unknown) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  }
>();

const DEFAULT_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

export function getSeoStrategyByBrandId(brandId: string): Promise<GetSeoStrategyByBrandIdRes> {
  return apiRequest<GetSeoStrategyByBrandIdRes>(`/seo-strategy/${brandId}`, 'GET', true);
}

function createSeoStrategySocketPromise(
  brandId: string,
  fireRequest: () => Promise<SaveSeoStrategyRes | RegenerateSeoStrategyRes>
): Promise<SeoStrategyUpdateSocketRes> {
  const socketPromise = new Promise<SeoStrategyUpdateSocketRes>(function (resolve, reject) {
    const timeoutId = setTimeout(function () {
      const pending = pendingSeoStrategyUpdates.get(brandId);
      if (pending != null) {
        // Fallback: GET the strategy to check if it already completed while we missed the socket
        getSeoStrategyByBrandId(brandId)
          .then(function (res) {
            if (res.data.strategy.status === SeoStrategyStatus.Success) {
              pending.resolve({
                contentId: brandId,
                status: AiStatus.Success,
                result: res.data,
                error: '',
              });
            } else if (res.data.strategy.status === SeoStrategyStatus.Error) {
              pending.reject(new InternalServerError('SEO strategy generation failed'));
            } else {
              pending.reject(
                new InternalServerError(
                  `SEO strategy generation timed out after ${DEFAULT_TIMEOUT_MS / 1000} seconds`
                )
              );
            }
          })
          .catch(function (err: unknown) {
            pending.reject(err);
          })
          .finally(function () {
            pendingSeoStrategyUpdates.delete(brandId);
          });
      }
    }, DEFAULT_TIMEOUT_MS);

    pendingSeoStrategyUpdates.set(brandId, {
      resolve: function (value) {
        clearTimeout(timeoutId);
        resolve(value);
      },
      reject: function (reason: unknown) {
        clearTimeout(timeoutId);
        reject(reason instanceof Error ? reason : new Error(String(reason)));
      },
      timeoutId,
    });
  });

  // Fire the HTTP request (don't await it)
  fireRequest().catch(function (err: unknown) {
    const pending = pendingSeoStrategyUpdates.get(brandId);
    if (pending != null) {
      clearTimeout(pending.timeoutId);
      pending.reject(err);
      pendingSeoStrategyUpdates.delete(brandId);
    }
  });

  return socketPromise;
}

export function saveSeoStrategy(
  brandId: SaveSeoStrategyReq['params']['brandId'],
  data: SaveSeoStrategyReq['body']
): Promise<SeoStrategyUpdateSocketRes> {
  const req = saveSeoStrategyReqSchema.parse({
    params: { brandId },
    query: null,
    body: data,
  });

  return createSeoStrategySocketPromise(brandId, function () {
    return apiRequest<SaveSeoStrategyRes>(
      `/seo-strategy/${req.params.brandId}`,
      'PATCH',
      true,
      req.body
    );
  });
}

export function regenerateSeoStrategy(brandId: string): Promise<SeoStrategyUpdateSocketRes> {
  return createSeoStrategySocketPromise(brandId, function () {
    return apiRequest<RegenerateSeoStrategyRes>(
      `/seo-strategy/${brandId}/regenerate`,
      'POST',
      true
    );
  });
}

// NOTE: Called by the socket router when SEO_STRATEGY_UPDATE arrives
export function onSeoStrategyUpdate(data: SeoStrategyUpdateSocketRes) {
  const { contentId, status } = data;
  const pending = pendingSeoStrategyUpdates.get(contentId);

  if (pending != null) {
    if (status === AiStatus.Success) {
      clearTimeout(pending.timeoutId);
      pending.resolve(data);
      pendingSeoStrategyUpdates.delete(contentId);
    } else if (status === AiStatus.Error) {
      clearTimeout(pending.timeoutId);
      pending.reject(new InternalServerError(data.error ?? 'SEO strategy generation failed'));
      pendingSeoStrategyUpdates.delete(contentId);
    }
    // For 'pending' status, keep waiting
  } else {
    // No pending entry — socket arrived after page reload or missed registration
    // Update the TanStack Query cache directly if we have a result
    if (data.result != null && status === AiStatus.Success) {
      const queryClient = getQueryClient();
      // Inline the query key to avoid a circular dependency with seo-strategy-queries.ts
      queryClient.setQueryData(['seo-strategy', 'brand', contentId], data.result);
    }
  }
}

export function cancelSeoStrategyUpdate(brandId: string) {
  const pending = pendingSeoStrategyUpdates.get(brandId);
  if (pending != null) {
    clearTimeout(pending.timeoutId);
    pending.reject(new InternalServerError('SEO strategy update cancelled'));
    pendingSeoStrategyUpdates.delete(brandId);
  }
}

export function searchSeoStrategyKeywords(
  brandId: string,
  query?: SearchSeoStrategyKeywordsReq['query']
): Promise<SearchSeoStrategyKeywordsRes> {
  const params = new URLSearchParams();
  if (query != null) {
    for (const [key, value] of Object.entries(query)) {
      if (value != null) {
        params.set(key, String(value));
      }
    }
  }
  const qs = params.toString();
  return apiRequest<SearchSeoStrategyKeywordsRes>(
    `/seo-strategy/${brandId}/keywords${qs.length > 0 ? `?${qs}` : ''}`,
    'GET',
    true
  );
}

export function createSeoStrategyKeyword(
  brandId: string,
  data: CreateSeoStrategyKeywordReq['body']
): Promise<CreateSeoStrategyKeywordRes> {
  return apiRequest<CreateSeoStrategyKeywordRes>(
    `/seo-strategy/${brandId}/keywords`,
    'POST',
    true,
    data
  );
}

export function updateSeoStrategyKeyword(
  brandId: string,
  keywordId: string,
  data: UpdateSeoStrategyKeywordReq['body']
): Promise<UpdateSeoStrategyKeywordRes> {
  return apiRequest<UpdateSeoStrategyKeywordRes>(
    `/seo-strategy/${brandId}/keywords/${keywordId}`,
    'PATCH',
    true,
    data
  );
}

export function removeSeoStrategyKeyword(
  brandId: string,
  keywordId: string
): Promise<RemoveSeoStrategyKeywordRes> {
  return apiRequest<RemoveSeoStrategyKeywordRes>(
    `/seo-strategy/${brandId}/keywords/${keywordId}`,
    'DELETE',
    true
  );
}

export function linkSeoStrategyKeyword(
  brandId: string,
  data: LinkSeoStrategyKeywordToContentReq['body']
): Promise<LinkSeoStrategyKeywordToContentRes> {
  return apiRequest<LinkSeoStrategyKeywordToContentRes>(
    `/seo-strategy/${brandId}/keywords/link`,
    'POST',
    true,
    data
  );
}

export function getActionPlanByBrandId(brandId: string): Promise<GetActionPlanByBrandIdRes> {
  return apiRequest<GetActionPlanByBrandIdRes>(`/seo-strategy/${brandId}/actions`, 'GET', true);
}

export function updateActionById(
  brandId: string,
  actionId: string,
  data: UpdateActionByIdReq['body']
): Promise<UpdateActionByIdRes> {
  return apiRequest<UpdateActionByIdRes>(
    `/seo-strategy/${brandId}/actions/${actionId}`,
    'POST',
    true,
    data
  );
}
